"use client";

import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { syncResult, type SyncQueryResult } from "@/types/async-protocol";
import type { ChatState } from "@/types/frontend-types";
import {
  useSessionDataContext,
  useSessionLiveContext,
  type SessionDataContextValue,
  type SessionLiveContextValue,
} from "./context";

export interface NavigationValue {
  focusedProductId: string | null;
  productStack: string[];
  selectedVersionId: string | null;
  canGoBack: boolean;
  focusProduct: SessionDataContextValue["actions"]["focusProduct"];
  navigateToProduct: SessionDataContextValue["actions"]["navigateToProduct"];
  goBack: SessionDataContextValue["actions"]["goBack"];
  goBackN: SessionDataContextValue["actions"]["goBackN"];
  selectVersion: SessionDataContextValue["actions"]["selectVersion"];
}

export function useNavigation(): SyncQueryResult<NavigationValue> {
  const context = useSessionDataContext();

  return syncResult<NavigationValue>({
    focusedProductId: context.navigation.focusedProductId,
    productStack: context.navigation.productStack,
    selectedVersionId: context.navigation.selectedVersionId,
    canGoBack: context.navigation.productStack.length > 0,
    focusProduct: context.actions.focusProduct,
    navigateToProduct: context.actions.navigateToProduct,
    goBack: context.actions.goBack,
    goBackN: context.actions.goBackN,
    selectVersion: context.actions.selectVersion,
  });
}

export interface ProductStackControl {
  depth: number;
  pop: () => boolean;
  popN: (count: number) => number;
  getDepthCount: () => number;
  resolveToDepth: (targetDepth: number) => number;
}

export function useProductStack(): SyncQueryResult<ProductStackControl> {
  const navigationQuery = useNavigation();
  const navigation = navigationQuery.data;
  const stackLength = !navigation ? 0 : navigation.productStack.length;
  const depthRef = useRef(stackLength);

  useLayoutEffect(() => {
    depthRef.current = stackLength;
  }, [stackLength]);

  const goBack = navigation?.goBack;
  const goBackN = navigation?.goBackN;

  const pop = useCallback((): boolean => {
    if (depthRef.current === 0 || !goBack) return false;
    goBack();
    return true;
  }, [goBack]);

  const popN = useCallback(
    (count: number): number => (goBackN ? goBackN(count) : 0),
    [goBackN],
  );

  const getDepthCount = useCallback((): number => depthRef.current, []);

  const resolveToDepth = useCallback(
    (targetDepth: number): number => {
      const normalizedTargetDepth = Math.max(0, targetDepth);
      const currentDepth = depthRef.current;
      if (normalizedTargetDepth >= currentDepth || !goBackN) return 0;
      return goBackN(currentDepth - normalizedTargetDepth);
    },
    [goBackN],
  );

  const control = useMemo<ProductStackControl>(
    () => ({
      depth: stackLength,
      pop,
      popN,
      getDepthCount,
      resolveToDepth,
    }),
    [stackLength, pop, popN, getDepthCount, resolveToDepth],
  );

  /* eslint-disable-next-line react-hooks/refs */
  return syncResult(control);
}

export interface ChatStateValue {
  mode: ChatState["mode"];
  selectedProductId: ChatState["selectedProductId"];
  targetVersionId: ChatState["targetVersionId"];
  targetMessageId: ChatState["targetMessageId"];
  targetRequestId: ChatState["targetRequestId"];
  indicatedMessageId: ChatState["indicatedMessageId"];
  indicationRequestId: ChatState["indicationRequestId"];
  chat: ChatState;
  chatMessages: SessionDataContextValue["chatMessages"];
  chatMessageIds: SessionDataContextValue["chatMessageIds"];
  chatCursor: SessionDataContextValue["chatCursor"];
  chatHasMore: SessionDataContextValue["chatHasMore"];
  chatLoadingMore: SessionDataContextValue["chatLoadingMore"];
  collabs: SessionDataContextValue["collabs"];
  collabsByProduct: SessionDataContextValue["collabsByProduct"];
  newMessageIds: SessionLiveContextValue["newMessageIds"];
  setChatMode: (
    mode: Parameters<SessionDataContextValue["actions"]["setChatMode"]>[0],
    productId?: string,
  ) => void;
  setChatTargetVersion: SessionDataContextValue["actions"]["setChatTargetVersion"];
  setChatTargetMessage: SessionDataContextValue["actions"]["setChatTargetMessage"];
  setChatIndicatedMessage: SessionDataContextValue["actions"]["setChatIndicatedMessage"];
  clearChatTargets: SessionDataContextValue["actions"]["clearChatTargets"];
  markChatReadUpTo: SessionDataContextValue["actions"]["markChatReadUpTo"];
  markCollabsReadUpTo: SessionDataContextValue["actions"]["markCollabsReadUpTo"];
  addOwnMessageIds: SessionDataContextValue["actions"]["addOwnMessageIds"];
  clearNewMessageIds: SessionDataContextValue["actions"]["clearNewMessageIds"];
  loadOlderMessages: () => void;
}

export function useChatState(): SyncQueryResult<ChatStateValue> {
  const context = useSessionDataContext();
  const { newMessageIds } = useSessionLiveContext();

  return syncResult<ChatStateValue>({
    ...context.chat,
    chat: context.chat,
    chatMessages: context.chatMessages,
    chatMessageIds: context.chatMessageIds,
    chatCursor: context.chatCursor,
    chatHasMore: context.chatHasMore,
    chatLoadingMore: context.chatLoadingMore,
    collabs: context.collabs,
    collabsByProduct: context.collabsByProduct,
    newMessageIds,
    setChatMode: (mode, productId) => {
      void context.actions.setChatMode(mode, productId);
    },
    setChatTargetVersion: context.actions.setChatTargetVersion,
    setChatTargetMessage: context.actions.setChatTargetMessage,
    setChatIndicatedMessage: context.actions.setChatIndicatedMessage,
    clearChatTargets: context.actions.clearChatTargets,
    markChatReadUpTo: context.actions.markChatReadUpTo,
    markCollabsReadUpTo: context.actions.markCollabsReadUpTo,
    addOwnMessageIds: context.actions.addOwnMessageIds,
    clearNewMessageIds: context.actions.clearNewMessageIds,
    loadOlderMessages: () => {
      void context.actions.loadOlderMessages();
    },
  });
}

export interface UnreadValue {
  unread: SessionDataContextValue["unread"];
  unreadCollabCounts: SessionDataContextValue["unreadCollabCounts"];
  productReads: SessionDataContextValue["productReads"];
  productsSeen: SessionDataContextValue["productsSeen"];
  mainChatScopeKey: SessionDataContextValue["mainChatScopeKey"];
  isCollabUnread: SessionDataContextValue["isCollabUnread"];
  markProductRead: (productId: string) => void;
  markAllChatAsRead: SessionDataContextValue["actions"]["markAllChatAsRead"];
  markAllCollabsAsRead: SessionDataContextValue["actions"]["markAllCollabsAsRead"];
  markChatReadUpTo: SessionDataContextValue["actions"]["markChatReadUpTo"];
  markCollabsReadUpTo: SessionDataContextValue["actions"]["markCollabsReadUpTo"];
}

export function useUnread(): SyncQueryResult<UnreadValue> {
  const context = useSessionDataContext();

  return syncResult<UnreadValue>({
    unread: context.unread,
    unreadCollabCounts: context.unreadCollabCounts,
    productReads: context.productReads,
    productsSeen: context.productsSeen,
    mainChatScopeKey: context.mainChatScopeKey,
    isCollabUnread: context.isCollabUnread,
    markProductRead: (productId: string) => {
      void context.actions.markProductRead(productId);
    },
    markAllChatAsRead: context.actions.markAllChatAsRead,
    markAllCollabsAsRead: context.actions.markAllCollabsAsRead,
    markChatReadUpTo: context.actions.markChatReadUpTo,
    markCollabsReadUpTo: context.actions.markCollabsReadUpTo,
  });
}
